import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { Line } from 'react-chartjs-2';
import Layout from '../components/Layout';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { useEmployeeStatus } from '../hooks/useEmployeeStatus';
import { NovedadesHistory } from '../components/NovedadesHistory';
import SessionsControl from '../components/SessionsControl';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useWebcamCapture } from '../hooks/useWebcamCapture';
import { WebcamCaptureViewer } from '../components/WebcamCaptureViewer';
import { ResetPassword } from '../components/ResetPassword';
import { Alert } from '../components/Alert';
import { LastWebcamCapture } from '../components/LastWebcamCapture';
import { useWebRTC } from '../hooks/useWebRTC';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface DashboardCard {
  id?: string;
  title: string;
  value: string | number;
  icon: React.ReactNode;
  link: string;
  linkText: string;
  bgColor: string;
  isOnline?: boolean;
  activeNovedad?: {
    tipo: string;
    fecha_inicio: string;
  } | null;
}

interface NovedadDB {
  id: string;
  empleado_id: string;
  fecha_hora_inicio: string;
  tipo_novedad: {
    nombre: string;
  };
}

interface AlertStats {
  total: number;
  responded: number;
  percentage: number;
}

interface AlertSettings {
  id: string;
  employee_id: string;
  interval_minutes: number;
  active: boolean;
  created_at: string;
}

interface Profile {
  role: 'employee' | 'supervisor';
  alert_settings: {
    interval_minutes: number;
    active: boolean;
    id: string;
    created_at: string;
  }[];
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { profile } = useProfile();
  const { employeeStatuses } = useEmployeeStatus();
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [alertStats, setAlertStats] = useState<AlertStats>({ total: 0, responded: 0, percentage: 0 });
  const [chartData, setChartData] = useState<any>(null);
  const [novedadesData, setNovedadesData] = useState<NovedadDB[] | null>(null);
  const [employeeShifts, setEmployeeShifts] = useState<Record<string, { 
    work_start_time: string; 
    work_end_time: string;
    interval_minutes: number;
  } | null>>({});
  const [userRole, setUserRole] = useState<'employee' | 'supervisor' | null>(null);
  const [alertSettings, setAlertSettings] = useState<AlertSettings | null>(null);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const { peerConnection, localStream, error: webrtcError } = useWebRTC({
    userId: user?.id || '',
    isInitiator: false,
    isSupervisor: false
  });

  const { error: webcamError, hasWebcam } = useWebcamCapture({
    videoRef,
    canvasRef,
    isEnabled: userRole === 'employee',
    intervalMinutes: alertSettings?.interval_minutes,
    alertId: alertSettings?.id,
    externalStream: localStream // Usar el stream de WebRTC
  });

  useEffect(() => {
    if (webcamError || webrtcError) {
      setAlert({
        message: webcamError || webrtcError || 'Error con la cámara web',
        type: 'error'
      });
    }
  }, [webcamError, webrtcError]);

  useEffect(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    const resizeCanvas = () => {
      if (video.videoWidth && video.videoHeight) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
      }
    };

    video.addEventListener('loadedmetadata', resizeCanvas);

    return () => {
      video.removeEventListener('loadedmetadata', resizeCanvas);
    };
  }, []);

  useEffect(() => {
    return () => {
      // Detener todos los streams de video
      if (videoRef.current?.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => {
          track.stop();
          console.log('Track stopped on cleanup:', track.label);
        });
        videoRef.current.srcObject = null;
      }

      // Limpiar canvas
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
        canvasRef.current.width = 0;
        canvasRef.current.height = 0;
      }
    };
  }, []);

  useEffect(() => {
    if (hasWebcam && userRole === 'employee' && alertSettings?.active) {
      const layoutRef = document.getElementById('layout-root')?.parentElement;
      if (layoutRef) {
        (layoutRef as any).__webcamRef = { cleanup: () => {} };
      }
    }
  }, [hasWebcam, userRole, alertSettings?.active]);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) return;

      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select(`
            *,
            alert_settings (*)
          `)
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (profile) {
          setUserRole(profile.role);
          const settings = profile.alert_settings?.[0];
          if (settings) {
            setAlertSettings({
              id: settings.id,
              employee_id: user.id,
              interval_minutes: settings.interval_minutes,
              active: settings.active,
              created_at: settings.created_at
            });
          }
        }
      } catch (err) {
        console.error('Error fetching user role:', err);
        setAlert({
          message: 'Error al cargar el perfil de usuario',
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [user]);

  useEffect(() => {
    if (user && userRole === 'employee' && alertSettings) {
      const newSettings: AlertSettings = {
        id: alertSettings.id,
        employee_id: user.id,
        interval_minutes: alertSettings.interval_minutes,
        active: alertSettings.active,
        created_at: alertSettings.created_at
      };

      // Solo actualizar si los valores son diferentes
      if (JSON.stringify(newSettings) !== JSON.stringify(alertSettings)) {
        setAlertSettings(newSettings);
      }
    } else {
      setAlertSettings(null);
    }
  }, [user, userRole, alertSettings?.interval_minutes, alertSettings?.active, alertSettings?.id]);

  useEffect(() => {
    const updateWebcamStatus = async () => {
      if (!user || userRole !== 'employee') return;

      try {
        // Actualizar el perfil
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            has_webcam: hasWebcam,
            is_active: true,
            webcam_last_checked: new Date().toISOString(),
            last_activity: new Date().toISOString()
          })
          .eq('id', user.id);

        if (updateError) {
          console.error('Error al actualizar el estado de la cámara:', updateError);
          return;
        }
      } catch (err) {
        console.error('Error al actualizar el estado de la cámara:', err);
      }
    };

    updateWebcamStatus();
  }, [user, hasWebcam, userRole]);

  useEffect(() => {
    return () => {
      const updateStatus = async () => {
        if (!user || userRole !== 'employee') return;
        
        try {
          // Actualizar el perfil
          const { error: updateError } = await supabase
            .from('profiles')
            .update({
              is_active: false,
              has_webcam: false,
              webcam_last_checked: new Date().toISOString(),
              last_activity: new Date().toISOString()
            })
            .eq('id', user.id);

          if (updateError) {
            console.error('Error al actualizar el estado de la cámara:', updateError);
            return;
          }
        } catch (err) {
          console.error('Error al actualizar el estado de la cámara:', err);
        }
      };

      void updateStatus();
    };
  }, [user, userRole]);

  const formatDateToColombia = (date: string) => {
    return new Date(date).toLocaleString('es-CO', {
      timeZone: 'America/Bogota',
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  useEffect(() => {
    if (profile?.role === 'supervisor') {
      const getEmployeeStatusStats = async () => {
        try {
          let query = supabase
            .from('employee_status')
            .select(`
              id,
              employee_id,
              status,
              last_status_change,
              profiles!employee_status_employee_id_fkey(name)
            `)
            .gte('last_status_change', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());

          if (selectedEmployee) {
            query = query.eq('employee_id', selectedEmployee);
          }

          const { data, error } = await query;

          if (error) throw error;

          const today = new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' }).split(',')[0];
          const todayStats = data.filter(record => 
            new Date(record.last_status_change).toLocaleString('es-CO', { timeZone: 'America/Bogota' }).split(',')[0] === today
          );

          const total = todayStats.length;
          const online = todayStats.filter(record => record.status).length;
          const percentage = total > 0 ? (online / total) * 100 : 0;

          setAlertStats({
            total,
            responded: online,
            percentage
          });

          const last7Days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - i);
            return date.toLocaleString('es-CO', { timeZone: 'America/Bogota' }).split(',')[0];
          }).reverse();

          const statusData = last7Days.map(date => {
            const dayRecords = data.filter(record => 
              new Date(record.last_status_change).toLocaleString('es-CO', { timeZone: 'America/Bogota' }).split(',')[0] === date
            );
            const onlineCount = dayRecords.filter(record => record.status).length;
            const totalCount = dayRecords.length;
            return totalCount > 0 ? (onlineCount / totalCount) * 100 : 0;
          });

          setChartData({
            labels: last7Days,
            datasets: [
              {
                label: 'Tiempo en Línea (%)',
                data: statusData,
                borderColor: 'rgb(75, 192, 192)',
                tension: 0.1,
                fill: false
              }
            ]
          });
        } catch (err) {
          setAlert({
            message: 'Error fetching employee status stats',
            type: 'error'
          });
        }
      };

      getEmployeeStatusStats();
    }
  }, [selectedEmployee, profile?.role]);

  const employeeCards: DashboardCard[] = [
    {
      title: 'Mis Tareas',
      value: '0',
      icon: <ClipboardDocumentListIcon className="h-6 w-6 text-white" />,
      link: '/tasks',
      linkText: 'Ver mis tareas',
      bgColor: 'bg-blue-500',
    },
    {
      title: 'Tiempo Activo',
      value: '0h',
      icon: <ClipboardDocumentListIcon className="h-6 w-6 text-white" />,
      link: '#',
      linkText: 'Detalles de actividad',
      bgColor: 'bg-orange-500',
    },
    {
      title: 'Mensajes',
      value: '0',
      icon: <ChatBubbleLeftRightIcon className="h-6 w-6 text-white" />,
      link: '/chat',
      linkText: 'Ver mensajes',
      bgColor: 'bg-yellow-500',
    },
  ];

  useEffect(() => {
    if (profile?.role === 'supervisor') {
      const fetchNovedades = async () => {
        try {
          const { data: novedadesData, error } = await supabase
            .from('registro_novedad')
            .select('*, tipo_novedad!inner(*)')
            .eq('estado', 'En curso') as { data: NovedadDB[] | null, error: any };

          if (error) throw error;

          setNovedadesData(novedadesData);
        } catch (error) {
          setAlert({
            message: 'Error fetching novedades',
            type: 'error'
          });
        }
      };

      const novedadesSubscription = supabase
        .channel('registro_novedad_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'registro_novedad'
          },
          async (payload) => {
            if (payload.eventType === 'INSERT' && payload.new.estado === 'En curso') {
              const { data, error } = await supabase
                .from('registro_novedad')
                .select('*, tipo_novedad!inner(*)')
                .eq('id', payload.new.id)
                .single() as { data: NovedadDB | null, error: any };

              if (error) {
                setAlert({
                  message: 'Error fetching nueva novedad',
                  type: 'error'
                });
                return;
              }

              if (data) {
                setNovedadesData(prevData => {
                  if (!prevData) return [data];
                  return [...prevData, data];
                });
              }
            } else if (payload.eventType === 'UPDATE' && payload.new.estado !== 'En curso') {
              setNovedadesData(prevData => 
                prevData ? prevData.filter(n => n.id !== payload.new.id) : null
              );
            } else if (payload.eventType === 'DELETE') {
              setNovedadesData(prevData => 
                prevData ? prevData.filter(n => n.id !== payload.old.id) : null
              );
            }
          }
        )
        .subscribe();

      fetchNovedades();

      return () => {
        novedadesSubscription.unsubscribe();
      };
    }
  }, [profile?.role]);

  const fetchEmployeeShifts = async (employeeIds: string[]) => {
    try {
      const { data, error } = await supabase
        .from('alert_settings')
        .select('employee_id, work_start_time, work_end_time, interval_minutes')
        .in('employee_id', employeeIds);

      if (error) throw error;

      const shiftsMap = data.reduce((acc, curr) => {
        acc[curr.employee_id] = {
          work_start_time: curr.work_start_time,
          work_end_time: curr.work_end_time,
          interval_minutes: curr.interval_minutes
        };
        return acc;
      }, {} as Record<string, { 
        work_start_time: string; 
        work_end_time: string;
        interval_minutes: number;
      }>);

      setEmployeeShifts(shiftsMap);
    } catch (error) {
      setAlert({
        message: 'Error fetching employee shifts',
        type: 'error'
      });
    }
  };

  useEffect(() => {
    if (employeeStatuses.length > 0) {
      const employeeIds = employeeStatuses.map(emp => emp.id);
      fetchEmployeeShifts(employeeIds);
    }
  }, [employeeStatuses]);

  const showWebcamElements = userRole === 'employee';

  const renderContent = () => {
    if (userRole === 'employee') {
      return (
        <div className="space-y-8">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Estado de Monitoreo</h2>
            <div className="relative w-full max-w-md mx-auto">
              <div className="relative overflow-hidden rounded-lg" style={{ aspectRatio: '16/9' }}>
                {showWebcamElements && (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="hidden"
                    />
                    <canvas
                      ref={canvasRef}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </>
                )}
              </div>
              <div className="mt-4 text-sm text-gray-600 flex items-center justify-end">
                <div className="flex items-center">
                  <div className={`w-2 h-2 rounded-full mr-2 ${hasWebcam ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span>{hasWebcam ? 'Cámara activa' : 'Cámara inactiva'}</span>
                </div>
              </div>
            </div>

            {/* Configuración de alertas */}
            {alertSettings && (
              <div className="mt-6">
                <h3 className="text-lg font-medium mb-2">Configuración de Alertas</h3>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <span className="mr-2">Intervalo (minutos):</span>
                    <input
                      type="number"
                      value={alertSettings.interval_minutes}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value) && value > 0) {
                          setAlertSettings({
                            ...alertSettings,
                            interval_minutes: value
                          });
                        }
                      }}
                      className="border rounded px-2 py-1 w-20"
                      min="1"
                    />
                  </label>
                  <label className="flex items-center">
                    <span className="mr-2">Activo:</span>
                    <input
                      type="checkbox"
                      checked={alertSettings.active}
                      onChange={(e) => {
                        setAlertSettings({
                          ...alertSettings,
                          active: e.target.checked
                        });
                      }}
                      className="form-checkbox h-4 w-4 text-blue-600"
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Panel de {userRole === 'supervisor' ? 'Supervisor' : 'Empleado'}</h1>
        
        {userRole === 'employee' && (
          <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
            <h2 className="text-xl font-semibold mb-4">Estado de Monitoreo</h2>
            <div className="relative w-full max-w-md mx-auto">
              <div className="relative overflow-hidden rounded-lg" style={{ aspectRatio: '16/9' }}>
                {showWebcamElements && (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="hidden"
                    />
                    <canvas
                      ref={canvasRef}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </>
                )}
              </div>
              <div className="mt-4 text-sm text-gray-600 flex items-center justify-end">
                <div className="flex items-center">
                  <div className={`w-2 h-2 rounded-full mr-2 ${hasWebcam ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span>{hasWebcam ? 'Cámara activa' : 'Cámara inactiva'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Resto del contenido del dashboard */}
        {userRole === 'supervisor' ? (
          <>
            {/* Métricas de resumen */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-indigo-500 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <UsersIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-white truncate">
                          Empleados Activos
                        </dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-white">
                            {employeeStatuses.filter(emp => emp.isOnline).length}
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                <div className="bg-indigo-600 px-5 py-3">
                  <div className="text-sm">
                    <Link
                      to="/employee-list"
                      className="font-medium text-white hover:text-indigo-100"
                    >
                      Ver todos los empleados
                    </Link>
                  </div>
                </div>
              </div>

              <div className="bg-green-500 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ChatBubbleLeftRightIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-white truncate">
                          Chats Activos
                        </dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-white">
                            0
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                <div className="bg-green-600 px-5 py-3">
                  <div className="text-sm">
                    <Link
                      to="/chat"
                      className="font-medium text-white hover:text-green-100"
                    >
                      Ver chats
                    </Link>
                  </div>
                </div>
              </div>

              <div className="bg-purple-500 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-white truncate">
                          Tareas Pendientes
                        </dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-white">
                            0
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-600 px-5 py-3">
                  <div className="text-sm">
                    <Link
                      to="/tasks"
                      className="font-medium text-white hover:text-purple-100"
                    >
                      Ver tareas
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de empleados */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {employeeStatuses.map((emp) => {
                const novedad = novedadesData?.find(
                  (n: NovedadDB) => n.empleado_id === emp.id
                );

                return (
                  <div 
                    key={emp.id} 
                    className={`bg-white shadow-lg rounded-lg overflow-hidden border-l-4 transition-all duration-300 hover:shadow-xl cursor-pointer ${
                      emp.isOnline ? 'border-l-green-500' : 'border-l-red-400'
                    } ${selectedEmployee === emp.id ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={() => setSelectedEmployee(emp.id)}
                  >
                    <div className="p-5">
                      {/* Encabezado con nombre y estado */}
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {emp.name || 'Sin nombre'}
                          <span
                            className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              emp.isOnline 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {emp.isOnline ? 'En línea' : 'Desconectado'}
                          </span>
                        </h3>
                      </div>

                      {/* Email */}
                      <div className="flex items-center text-gray-500 mb-4">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm truncate">{emp.email}</span>
                      </div>

                      {/* Información del empleado */}
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <span className="text-sm text-gray-500">Turno</span>
                            <span className="text-sm text-gray-900 ml-2">
                              {employeeShifts[emp.id] && employeeShifts[emp.id]?.work_start_time && employeeShifts[emp.id]?.work_end_time
                                ? `${employeeShifts[emp.id]?.work_start_time} - ${employeeShifts[emp.id]?.work_end_time}`
                                : "No definido"}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <span className="text-sm text-gray-500">Última actividad</span>
                            <span className="text-sm text-gray-900 ml-2">
                              {emp.last_activity
                                ? formatDateToColombia(emp.last_activity)
                                : 'N/A'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <span className="text-sm text-gray-500">Intervalo</span>
                            <span className="text-sm text-gray-900 ml-2">
                              {employeeShifts[emp.id] && employeeShifts[emp.id]?.interval_minutes
                                ? `${employeeShifts[emp.id]?.interval_minutes} minutos`
                                : "No definido"}
                            </span>
                          </div>
                        </div>

                        {/* Novedad activa */}
                        {novedad && (
                          <div className="mt-3">
                            <div className="bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
                              <div className="flex items-center">
                                <svg className="w-4 h-4 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div>
                                  <span className="text-sm text-yellow-800">
                                    {novedad.tipo_novedad.nombre}
                                  </span>
                                  <span className="text-sm text-yellow-600 ml-2">
                                    Desde: {formatDateToColombia(novedad.fecha_hora_inicio)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Alert Statistics */}
            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h2 className="text-xl font-semibold mb-4">
                Estadísticas de Estado Diarias
                {selectedEmployee && ` - ${employeeStatuses.find(e => e.id === selectedEmployee)?.name}`}
              </h2>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-800">Total de Cambios de Estado</h3>
                  <p className="mt-2 text-3xl font-semibold text-blue-900">{alertStats.total}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-green-800">Tiempo en Línea</h3>
                  <p className="mt-2 text-3xl font-semibold text-green-900">{alertStats.responded}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-purple-800">Porcentaje en Línea</h3>
                  <p className="mt-2 text-3xl font-semibold text-purple-900">
                    {alertStats.percentage.toFixed(1)}%
                  </p>
                </div>
              </div>

              {/* Charts */}
              {chartData && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium mb-4">Tendencia de Tiempo en Línea (Últimos 7 días)</h3>
                  <div className="h-64">
                    <Line
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Porcentaje Diario de Tiempo en Línea',
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            max: 100,
                            ticks: {
                              callback: function(value) {
                                return value + '%';
                              }
                            }
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Panel de acciones para el empleado seleccionado */}
            {selectedEmployee && (
              <div className="mt-6 space-y-4">
                <div className="bg-white p-4 rounded-lg shadow">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Acciones</h3>
                  <div className="space-y-4">
                    {/* Restablecer contraseña */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Restablecer Contraseña</h4>
                      <ResetPassword 
                        onSuccess={() => {
                          setAlert({
                            type: 'success',
                            message: 'Contraseña restablecida exitosamente'
                          });
                        }}
                        onError={(error) => {
                          setAlert({
                            type: 'error',
                            message: `Error al restablecer la contraseña: ${error}`
                          });
                        }}
                      />
                    </div>

                    {/* Otras acciones aquí */}
                  </div>
                </div>
              </div>
            )}

            {/* Session Control Section */}
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">Control de Sesiones de Empleados</h2>
              <SessionsControl isSupervisor={true} />
            </div>

            {webcamError && (
              <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-4">
                Error con la webcam: {webcamError}
              </div>
            )}

            {/* Mensaje de estado del sistema para empleados */}
            {profile?.role === 'employee' && hasWebcam && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-blue-700">
                  Sistema de monitoreo {alertSettings?.active ? 'activo' : 'inactivo'}. 
                </p>
                <div className="relative mt-4">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-auto rounded-lg"
                    style={{ display: 'none' }}
                  />
                  <canvas
                    ref={canvasRef}
                    className="w-full h-auto rounded-lg"
                  />
                </div>
              </div>
            )}

            {profile?.role === 'employee' && webcamError && (
              <div className="bg-red-50 text-red-500 p-4 rounded-lg mt-4">
                Error con la webcam: {webcamError}
              </div>
            )}
          </>
        ) : (
          employeeCards.map((card, index) => (
            <div key={index} className={`${card.bgColor} overflow-hidden shadow rounded-lg`}>
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">{card.icon}</div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-white truncate">{card.title}</dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-white">{card.value}</div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
              <Link
                to={card.link}
                className="bg-gray-50 px-5 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                {card.linkText}
              </Link>
            </div>
          ))
        )}

        {/* Historial de Novedades para empleados */}
        {profile?.role === 'employee' && user && (
          <div className="mt-6">
            <NovedadesHistory empleadoId={user.id} />
          </div>
        )}
      </div>
    </Layout>
  );
}
